var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable @typescript-eslint/consistent-type-definitions */
import * as React from 'react';
import { useRef, useState } from 'react';
import classNames from 'classnames';
import useLayoutEffect from 'rc-util/lib/hooks/useLayoutEffect';
import Filler from './Filler';
import ScrollBar from './ScrollBar';
import useChildren from './hooks/useChildren';
import useHeights from './hooks/useHeights';
import useScrollTo from './hooks/useScrollTo';
import useDiffItem from './hooks/useDiffItem';
import useFrameWheel from './hooks/useFrameWheel';
import useMobileTouchMove from './hooks/useMobileTouchMove';
import useOriginScroll from './hooks/useOriginScroll';
const EMPTY_DATA = [];
const ScrollStyle = {
    overflowY: 'auto',
    overflowAnchor: 'none'
};
export function RawList(props, ref) {
    const { prefixCls = 'virtual-list', className, height, itemHeight, fullHeight = true, style, data, children, itemKey, virtual, component: Component = 'div', onScroll, onVisibleChange } = props, restProps = __rest(props, ["prefixCls", "className", "height", "itemHeight", "fullHeight", "style", "data", "children", "itemKey", "virtual", "component", "onScroll", "onVisibleChange"]);
    // ================================= MISC =================================
    const useVirtual = !!(virtual !== false && height && itemHeight);
    const inVirtual = useVirtual && data && itemHeight * data.length > height;
    const [scrollTop, setScrollTop] = useState(0);
    const [scrollMoving, setScrollMoving] = useState(false);
    const mergedClassName = classNames(prefixCls, className);
    const mergedData = data || EMPTY_DATA;
    const componentRef = useRef();
    const fillerInnerRef = useRef();
    const scrollBarRef = useRef(); // Hack on scrollbar to enable flash call
    // =============================== Item Key ===============================
    const getKey = React.useCallback((item) => {
        if (typeof itemKey === 'function') {
            return itemKey(item);
        }
        return item === null || item === void 0 ? void 0 : item[itemKey];
    }, [itemKey]);
    const sharedConfig = {
        getKey
    };
    // ================================ Scroll ================================
    function syncScrollTop(newTop) {
        setScrollTop(origin => {
            let value;
            if (typeof newTop === 'function') {
                value = newTop(origin);
            }
            else {
                value = newTop;
            }
            const alignedTop = keepInRange(value);
            componentRef.current.scrollTop = alignedTop;
            return alignedTop;
        });
    }
    // ================================ Legacy ================================
    // Put ref here since the range is generate by follow
    const rangeRef = useRef({ start: 0, end: mergedData.length });
    const diffItemRef = useRef();
    const [diffItem] = useDiffItem(mergedData, getKey);
    diffItemRef.current = diffItem;
    // ================================ Height ================================
    const [setInstanceRef, collectHeight, heights, heightUpdatedMark] = useHeights(getKey, null, null);
    // ========================== Visible Calculation =========================
    const { scrollHeight, start, end, offset } = React.useMemo(() => {
        var _a;
        if (!useVirtual) {
            return {
                scrollHeight: undefined,
                start: 0,
                end: mergedData.length - 1,
                offset: undefined
            };
        }
        // Always use virtual scroll bar in avoid shaking
        if (!inVirtual) {
            return {
                scrollHeight: ((_a = fillerInnerRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 0,
                start: 0,
                end: mergedData.length - 1,
                offset: undefined
            };
        }
        let itemTop = 0;
        let startIndex;
        let startOffset;
        let endIndex;
        const dataLen = mergedData.length;
        for (let i = 0; i < dataLen; i += 1) {
            const item = mergedData[i];
            const key = getKey(item);
            const cacheHeight = heights.get(key);
            const currentItemBottom = itemTop + (cacheHeight === undefined ? itemHeight : cacheHeight);
            // Check item top in the range
            if (currentItemBottom >= scrollTop && startIndex === undefined) {
                startIndex = i;
                startOffset = itemTop;
            }
            // Check item bottom in the range. We will render additional one item for motion usage
            if (currentItemBottom > scrollTop + height && endIndex === undefined) {
                endIndex = i;
            }
            itemTop = currentItemBottom;
        }
        // Fallback to normal if not match. This code should never reach
        /* istanbul ignore next */
        if (startIndex === undefined) {
            startIndex = 0;
            startOffset = 0;
        }
        if (endIndex === undefined) {
            endIndex = mergedData.length - 1;
        }
        // Give cache to improve scroll experience
        endIndex = Math.min(endIndex + 1, mergedData.length);
        return {
            scrollHeight: itemTop,
            start: startIndex,
            end: endIndex,
            offset: startOffset
        };
    }, [inVirtual, useVirtual, scrollTop, mergedData, heightUpdatedMark, height]);
    rangeRef.current.start = start;
    rangeRef.current.end = end;
    // =============================== In Range ===============================
    const maxScrollHeight = scrollHeight - height;
    const maxScrollHeightRef = useRef(maxScrollHeight);
    maxScrollHeightRef.current = maxScrollHeight;
    function keepInRange(newScrollTop) {
        let newTop = newScrollTop;
        if (!Number.isNaN(maxScrollHeightRef.current)) {
            newTop = Math.min(newTop, maxScrollHeightRef.current);
        }
        newTop = Math.max(newTop, 0);
        return newTop;
    }
    const isScrollAtTop = scrollTop <= 0;
    const isScrollAtBottom = scrollTop >= maxScrollHeight;
    const originScroll = useOriginScroll(isScrollAtTop, isScrollAtBottom);
    // ================================ Scroll ================================
    function onScrollBar(newScrollTop) {
        const newTop = newScrollTop;
        syncScrollTop(newTop);
    }
    // When data size reduce. It may trigger native scroll event back to fit scroll position
    function onFallbackScroll(e) {
        const { scrollTop: newScrollTop } = e.currentTarget;
        if (newScrollTop !== scrollTop) {
            syncScrollTop(newScrollTop);
        }
        // Trigger origin onScroll
        onScroll === null || onScroll === void 0 ? void 0 : onScroll(e);
    }
    // Since this added in global,should use ref to keep update
    const [onRawWheel, onFireFoxScroll] = useFrameWheel(useVirtual, isScrollAtTop, isScrollAtBottom, offsetY => {
        syncScrollTop(top => {
            const newTop = top + offsetY;
            return newTop;
        });
    });
    // Mobile touch move
    useMobileTouchMove(useVirtual, componentRef, (deltaY, smoothOffset) => {
        if (originScroll(deltaY, smoothOffset)) {
            return false;
        }
        onRawWheel({ preventDefault() { }, deltaY });
        return true;
    });
    useLayoutEffect(() => {
        // Firefox only
        function onMozMousePixelScroll(e) {
            if (useVirtual) {
                e.preventDefault();
            }
        }
        componentRef.current.addEventListener('wheel', onRawWheel);
        componentRef.current.addEventListener('DOMMouseScroll', onFireFoxScroll);
        componentRef.current.addEventListener('MozMousePixelScroll', onMozMousePixelScroll);
        return () => {
            componentRef.current.removeEventListener('wheel', onRawWheel);
            componentRef.current.removeEventListener('DOMMouseScroll', onFireFoxScroll);
            componentRef.current.removeEventListener('MozMousePixelScroll', onMozMousePixelScroll);
        };
    }, [useVirtual]);
    // ================================= Ref ==================================
    const scrollTo = useScrollTo(componentRef, mergedData, heights, itemHeight, getKey, collectHeight, syncScrollTop, () => {
        var _a;
        (_a = scrollBarRef.current) === null || _a === void 0 ? void 0 : _a.delayHidden();
    });
    React.useImperativeHandle(ref, () => ({
        scrollTo,
        componentRef
    }));
    // ================================ Effect ================================
    /** We need told outside that some list not rendered */
    useLayoutEffect(() => {
        if (onVisibleChange) {
            const renderList = mergedData.slice(start, end + 1);
            onVisibleChange(renderList, mergedData);
        }
    }, [start, end, mergedData]);
    // ================================ Render ================================
    const listChildren = useChildren(mergedData, start, end, setInstanceRef, children, sharedConfig);
    let componentStyle = null;
    if (height) {
        componentStyle = Object.assign({ [fullHeight ? 'height' : 'maxHeight']: height }, ScrollStyle);
        if (useVirtual) {
            componentStyle.overflowY = 'hidden';
            if (scrollMoving) {
                componentStyle.pointerEvents = 'none';
            }
        }
    }
    return (<div style={Object.assign(Object.assign({}, style), { position: 'relative' })} className={mergedClassName} {...restProps}>
            <Component className={`${prefixCls}-holder`} style={componentStyle} ref={componentRef} onScroll={onFallbackScroll}>
                <Filler prefixCls={prefixCls} height={scrollHeight} offset={offset} ref={fillerInnerRef} onInnerResize={collectHeight}>
                    {listChildren}
                </Filler>
            </Component>

            {useVirtual && (<ScrollBar ref={scrollBarRef} prefixCls={prefixCls} scrollTop={scrollTop} height={height} scrollHeight={scrollHeight} count={mergedData.length} onScroll={onScrollBar} onStartMove={() => {
                setScrollMoving(true);
            }} onStopMove={() => {
                setScrollMoving(false);
            }}/>)}
        </div>);
}
const List = React.forwardRef(RawList);
List.displayName = 'List';
export default List;
